import { type FC, type ReactNode, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { type SchedulingEventType } from '../../../models/Scheduling'
import { SettingsRow } from '../../Setup/Scheduling/sections/SettingsSection'
import { PlaceChip } from '../rows/PlaceChip'
import { BookingActionMenu } from '../rows/BookingActionMenu'
import styles from './EventTypeList.module.scss'

const COLLAPSED_ROW_COUNT = 5

interface EventTypeListProps {
  eventTypes: SchedulingEventType[]
  readOnly?: boolean
  rowChip?: (eventType: SchedulingEventType) => ReactNode
  editLabel?: string
  onEdit?: (eventType: SchedulingEventType) => void
  onPreview?: (eventType: SchedulingEventType) => void
  onCopyLink?: (eventType: SchedulingEventType) => void
  onRemoveFromPage?: (eventType: SchedulingEventType) => void
  onDuplicate?: (eventType: SchedulingEventType) => void
  onDelete?: (eventType: SchedulingEventType) => void
}

const StatusChip: FC<{ isActive: boolean }> = ({ isActive }) =>
  isActive ? (
    <span className='d-inline-flex align-items-center zocks-gap-0.25 badge rounded-3 zocks-bg-color-success-background zocks-text-success'>
      <i className={classNames('bi bi-circle-fill', styles.statusDot)} />
      Active
    </span>
  ) : (
    <span className='badge rounded-3 zocks-bg-color-gray-200 zocks-text-gray-600'>Inactive</span>
  )

export const EventTypeList: FC<EventTypeListProps> = ({
  eventTypes,
  readOnly = false,
  rowChip,
  editLabel = 'Edit',
  onEdit,
  onPreview,
  onCopyLink,
  onRemoveFromPage,
  onDuplicate,
  onDelete,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const visible = isExpanded ? eventTypes : eventTypes.slice(0, COLLAPSED_ROW_COUNT)
  const hiddenCount = eventTypes.length - visible.length

  return (
    <>
      {visible.map((eventType) => (
        <SettingsRow key={eventType.id} className='d-flex align-items-center gap-3'>
          <div className='d-flex flex-column flex-grow-1 zocks-gap-0.25'>
            <div className='d-flex align-items-center flex-wrap zocks-gap-0.5'>
              <span className='bold'>{eventType.name}</span>
              <span className='zocks-text-gray-600'>·</span>
              <span className='paragraph-small zocks-text-gray-600'>{eventType.durationMinutes} min</span>
              <PlaceChip meetingMode={eventType.meetingMode} />
              {rowChip ? rowChip(eventType) : null}
            </div>
            {eventType.clientFacingText ? <span className='paragraph-small zocks-text-gray-600'>{eventType.clientFacingText}</span> : null}
          </div>
          <StatusChip isActive={eventType.active} />
          {onPreview ? (
            <Button variant='borderlessGrayButton' className='p-2' aria-label={`Preview ${eventType.name}`} onClick={() => onPreview(eventType)}>
              <i className='bi bi-box-arrow-up-right' />
            </Button>
          ) : null}
          {onEdit ? (
            <Button variant='grayButton' onClick={() => onEdit(eventType)}>
              {readOnly ? 'View' : editLabel}
            </Button>
          ) : null}
          {!readOnly && (onCopyLink || onRemoveFromPage || onDuplicate || onDelete) ? (
            <BookingActionMenu ariaLabel={`${eventType.name} actions`}>
              {onCopyLink ? (
                <Dropdown.Item onClick={() => onCopyLink(eventType)}>
                  <i className='bi bi-copy me-1' /> Copy booking link
                </Dropdown.Item>
              ) : null}
              {onDuplicate ? (
                <Dropdown.Item onClick={() => onDuplicate(eventType)}>
                  <i className='bi bi-copy me-1' /> Duplicate
                </Dropdown.Item>
              ) : null}
              {onRemoveFromPage ? (
                <Dropdown.Item className='text-danger' onClick={() => onRemoveFromPage(eventType)}>
                  <i className='bi bi-trash me-1' /> Remove from booking page
                </Dropdown.Item>
              ) : null}
              {onDelete ? (
                <Dropdown.Item className='text-danger' onClick={() => onDelete(eventType)}>
                  <i className='bi bi-trash me-1' /> Delete
                </Dropdown.Item>
              ) : null}
            </BookingActionMenu>
          ) : null}
        </SettingsRow>
      ))}
      {isExpanded && eventTypes.length > COLLAPSED_ROW_COUNT ? (
        <SettingsRow className='d-flex justify-content-center'>
          <Button variant='link' onClick={() => setIsExpanded(false)}>
            Show less
            <i className='bi bi-chevron-up ms-1' />
          </Button>
        </SettingsRow>
      ) : hiddenCount > 0 ? (
        <SettingsRow className='d-flex justify-content-center'>
          <Button variant='link' onClick={() => setIsExpanded(true)}>
            Show {hiddenCount} more
            <i className='bi bi-chevron-down ms-1' />
          </Button>
        </SettingsRow>
      ) : null}
    </>
  )
}
